import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { authEndpoints } from "@src/services/endpoints";
import SignolUtils from "@src/utils/signolUtils";
import StorageUtils from "@src/utils/storageUtils";
import { AppHeader, AppFooter, NavMenu, ScrollToTop } from "./index";
import { PageErrorContext } from "@src/context/pageErrorContext";
import { useDCContext } from "@src/context/dcContext";
import { useAuthContext } from "@src/context/authContext";
import { HeaderCalloutProvider } from "@src/context/headerCalloutContext";
import { GlobalMessageBanner } from "../atoms";
import { IMPERSONATED_SESSION_COOKIE } from "@src/constants/constants";
import packageJson from "../../../package.json" with { type: "json" };
import ErrorApp from "./ErrorApp";
import { useIsWideDesktopWidth } from "../../utils";
import NavMenu2026 from "./NavMenu2026";

const DefaultAppLayout = (props) => {
  const { containerWidth = "1120px", navItems = [], currentPage } = props;
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const navigateTo = useNavigate();
  const { updateError } = useContext(PageErrorContext);
  const dc = useDCContext();
  const auth = useAuthContext();
  const navMenuRef = useRef(null);
  const isWideDesktop = useIsWideDesktopWidth();

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        navMenuRef.current &&
        !navMenuRef.current.contains(event.target) &&
        navMenuOpen
      ) {
        setNavMenuOpen(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [navMenuRef, setNavMenuOpen, navMenuOpen]);

  const impersonateLogout = useMutation({
    mutationKey: ["IMPERSONATE_LOGOUT"],
    mutationFn: () => {
      const impersonatedSession = StorageUtils.getObjectItem(
        IMPERSONATED_SESSION_COOKIE,
      );
      const impersonatedSessionId = impersonatedSession?.admin?.id;
      if (impersonatedSessionId)
        return authEndpoints.impersonateLogout(impersonatedSession?.admin?.id);
    },
    onSuccess: (res) => {
      auth.impersonatedSessionLogout(res);
      window.location.href = "/dashboard"; // force reload
    },
  });

  if (!auth) return <ErrorApp />;
  const handleMenuClick = (newRoute) => {
    updateError(false);
    setNavMenuOpen(false);
    navigateTo(`${auth.activeRoleRoutePrefix}/${newRoute}`);
  };

  const decoratedNavItems = navItems.map((item) => {
    if (!!item.menuIconIsIndustryIcon) {
      item = {
        ...item,
        menuIcon: SignolUtils.getIndustryIcon(SignolUtils.getIndustry(), false),
        menuIconActive: SignolUtils.getIndustryIcon(
          SignolUtils.getIndustry(),
          true,
        ),
      };
    }
    if (!!item.pageTitleFromDynamicContent) {
      item = {
        ...item,
        pageTitle: dc.getDynamicReplacement(
          item.pageTitleFromDynamicContent,
          {},
          item.pageTitle,
          true,
        ),
      };
    }
    return item;
  });

  /** New Navigation UI, January 2026 */
  const enableNewNav2026 = decoratedNavItems.length > 1 && isWideDesktop;

  /** "You remain in charge. You decide when it is safe and practical to save fuel." */
  const safetyMessageBanner = auth.isActiveRoleOperator ? (
    <div
      className={`mt-auto py-6 px-6 max-w-7xl mx-auto sm:p-6 w-full bg-primary-200 bg-opacity-25`}
      style={{ maxWidth: `${containerWidth}` }}
    >
      <div className="mx-auto max-w-2xl">
        <div className="text-sm text-center">
          {dc.getDynamicReplacement("captain__overview__safety_message", "")}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <HeaderCalloutProvider>
      <div className="flex flex-col items-center bg-gutter min-h-screen">
        <ScrollToTop />
        <AppHeader
          showMenu={decoratedNavItems.length > 1 && !enableNewNav2026}
          containerWidth={containerWidth}
          logoSrc={`https://signol-email-images.s3.eu-west-2.amazonaws.com/customers/${auth.user?.customerCode?.toUpperCase()}/logo_${auth.user?.customerCode?.toUpperCase()}_customer.png`}
          signolLogoSrc="https://signol-email-images.s3.eu-west-2.amazonaws.com/customers/SG/logo_SG_customer.png"
          avatarInitials={auth.avatarInitials}
          toggleNavigationMenu={() => setNavMenuOpen(!navMenuOpen)}
          navigationMenuOpen={navMenuOpen}
          onSwitchRole={
            !!auth.hasRoleSwitcher() ? auth.selectUserProfile : false
          }
          onImpersonatedSession={
            // logout from impersonated session
            auth.impersonatedSessionContext ? impersonateLogout.mutate : false
          }
          onLogout={() => auth.logout()}
          onNavigate={handleMenuClick}
        />
        {auth.isActiveRoleOperator && <GlobalMessageBanner />}
        <div
          id="Signol_MainContainer"
          className="flex grow w-full relative bg-canvas h-full"
          style={{ maxWidth: `${containerWidth}` }}
        >
          {/* MOBILE & DESKTOP navigation menu behaves different depending on screen size */}
          <div ref={navMenuRef} className="z-49">
            {enableNewNav2026 && (
              <NavMenu2026
                currentPage={currentPage}
                items={decoratedNavItems}
                navMenuOpen={navMenuOpen}
                setNavMenuOpen={setNavMenuOpen}
                onItemSelect={handleMenuClick}
                onImpersonatedSession={
                  // logout from impersonated session
                  auth.impersonatedSessionContext
                    ? impersonateLogout.mutate
                    : false
                }
              />
            )}
            {!enableNewNav2026 && (
              <div
                className={`top-0 z-49
                ${
                  navMenuOpen
                    ? "w-full md:w-3/12 md:absolute md:left-0 md:inset-y-0"
                    : "hidden"
                }`}
              >
                <NavMenu
                  user={auth.user}
                  avatarInitials={auth.avatarInitials}
                  currentPage={currentPage}
                  items={navItems}
                  displayNavMenu={navMenuOpen}
                  onItemSelect={handleMenuClick}
                  setDisplayNavMenu={setNavMenuOpen}
                  onLogout={auth.logout}
                  onSwitchRole={
                    !!auth.hasRoleSwitcher() ? auth.selectUserProfile : false
                  }
                  onImpersonatedSession={
                    // logout from impersonated session
                    auth.impersonatedSessionContext
                      ? impersonateLogout.mutate
                      : false
                  }
                />
              </div>
            )}
          </div>
          <div
            className={`w-full min-h-full grow flex flex-col ${
              navMenuOpen ? "hidden md:block" : ""
            }`}
          >
            {props.children}
            {enableNewNav2026 && safetyMessageBanner}
          </div>
        </div>
        {!enableNewNav2026 && safetyMessageBanner}
        <AppFooter currentAppVersion={packageJson.version} />
      </div>
    </HeaderCalloutProvider>
  );
};

export default DefaultAppLayout;
